import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Fetches the current user's feature matrix row once per session + exposes a
 * simple boolean check. Used to hide nav items and gate UI based on admin's
 * functional-control settings.
 */
export function useFeatures() {
  const { user } = useAuth();
  const [features, setFeatures] = useState(null); // { key: {enabled, limit} }
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) { setFeatures(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/me/features");
      setFeatures(data.features || {});
    } catch {
      setFeatures({});
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const isEnabled = useCallback((key) => {
    // No features loaded yet — don't hide anything (prevents flash-of-empty-nav)
    if (features == null) return true;
    return Boolean(features[key]?.enabled);
  }, [features]);

  const limit = useCallback((key) => {
    return features?.[key]?.limit ?? null;
  }, [features]);

  return { features, isEnabled, limit, loading, reload: load };
}
